import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, getDocs, collection } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, Select, MenuItem, ListSubheader,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';

// ── Helpers ──────────────────────────────────────────────────────────────────

function uid() {
  return Date.now().toString() + Math.random().toString(36).slice(2);
}

function fmtSEK(val) {
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

function fmtMonth(ym) {
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const [y, m] = ym.split('-');
  return names[parseInt(m, 10) - 1] + ' ' + y.slice(2);
}

function generateMonths(start, end) {
  if (!start || !end) return [];
  const months = [];
  let [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  while ((sy < ey || (sy === ey && sm <= em)) && months.length < 36) {
    months.push(`${sy}-${String(sm).padStart(2, '0')}`);
    sm++;
    if (sm > 12) { sm = 1; sy++; }
  }
  return months;
}

function fmtResName(res, mode) {
  const name = typeof res === 'string' ? res : (res?.name || '');
  const shortName = typeof res === 'object' ? (res?.shortName || '') : '';
  if (!name && !shortName) return '(namnlös)';
  if (mode === 'first') return name.split(' ')[0] || shortName;
  if (mode === 'abbr') {
    if (shortName) return shortName;
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 4).toUpperCase();
    return (parts[0].slice(0, 2) + parts[parts.length - 1].slice(0, 2)).toUpperCase();
  }
  return name;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function OfferBudgetEditor() {
  const { offerId } = useParams();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Budget state – same structure as ProjectDetail
  const [budgets, setBudgets] = useState([]);
  const [activeBudgetId, setActiveBudgetId] = useState(null);

  // UI state
  const [resNameMode, setResNameMode] = useState('full');
  const [newResourceUserId, setNewResourceUserId] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [addMode, setAddMode] = useState('person'); // 'person' | 'role'

  // Column resize
  const [actColW, setActColW] = useState(null);
  const [resColW, setResColW] = useState(150);
  const dragColRef = useRef(null);
  const actColRef = useRef(null);
  const actColRenderedRef = useRef(180);
  const [actColRenderedW, setActColRenderedW] = useState(180);

  // Fill drag
  const fillDragRef = useRef(null);
  const fillRangeRef = useRef(null);
  const [fillHighlight, setFillHighlight] = useState(null);

  // Column copy
  const colCopyDragRef = useRef(null);
  const colCopyTargetRef = useRef(null);
  const [colCopyState, setColCopyState] = useState(null);

  // ── ResizeObserver for act col ─────────────────────────────────────────────
  useEffect(() => {
    const el = actColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.borderBoxSize?.[0]?.inlineSize || entry.contentRect.width);
      actColRenderedRef.current = w;
      setActColRenderedW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Column resize drag ─────────────────────────────────────────────────────
  const startColResize = useCallback((col, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = col === 'act'
      ? (actColW !== null ? actColW : actColRenderedRef.current)
      : resColW;
    dragColRef.current = { col, startX, startW };
    const onMove = (ev) => {
      if (!dragColRef.current) return;
      const delta = ev.clientX - dragColRef.current.startX;
      const newW = Math.max(80, Math.min(400, dragColRef.current.startW + delta));
      if (dragColRef.current.col === 'act') setActColW(newW);
      else setResColW(newW);
    };
    const onUp = () => {
      dragColRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [actColW, resColW]);

  // ── Fill drag (Excel-style) ────────────────────────────────────────────────
  function startFillDrag(actId, anchorIdx, value, months, e) {
    e.preventDefault();
    e.stopPropagation();
    fillDragRef.current = { actId, anchorIdx, value };
    fillRangeRef.current = { actId, fromIdx: anchorIdx, toIdx: anchorIdx };
    setFillHighlight({ actId, fromIdx: anchorIdx, toIdx: anchorIdx });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'crosshair';
    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (fillDragRef.current && fillRangeRef.current) {
        const { actId: aid, value: val } = fillDragRef.current;
        const { fromIdx, toIdx } = fillRangeRef.current;
        const newMonths = {};
        months.slice(fromIdx, toIdx + 1).forEach(m => { newMonths[m] = parseFloat(val) || 0; });
        updateActiveBudget(b => ({
          activities: b.activities.map(a =>
            a.id === aid ? { ...a, months: { ...a.months, ...newMonths } } : a
          ),
        }));
      }
      fillDragRef.current = null;
      fillRangeRef.current = null;
      setFillHighlight(null);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mouseup', onUp);
  }

  function onFillCellEnter(actId, monthIdx) {
    if (!fillDragRef.current || fillDragRef.current.actId !== actId) return;
    const anchorIdx = fillDragRef.current.anchorIdx;
    fillRangeRef.current = { actId, fromIdx: Math.min(anchorIdx, monthIdx), toIdx: Math.max(anchorIdx, monthIdx) };
    setFillHighlight({ actId, fromIdx: Math.min(anchorIdx, monthIdx), toIdx: Math.max(anchorIdx, monthIdx) });
  }

  // ── Column copy drag ───────────────────────────────────────────────────────
  function startColCopyDrag(sourceIdx, months, e) {
    e.preventDefault();
    colCopyDragRef.current = { sourceIdx, months };
    colCopyTargetRef.current = sourceIdx;
    setColCopyState({ sourceIdx, targetIdx: sourceIdx });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'copy';
    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      const tgt = colCopyTargetRef.current;
      const src = colCopyDragRef.current?.sourceIdx;
      const mo = colCopyDragRef.current?.months;
      if (mo && tgt !== undefined && tgt !== src) {
        const srcMonth = mo[src];
        const tgtMonth = mo[tgt];
        updateActiveBudget(b => ({
          activities: b.activities.map(a => ({
            ...a,
            months: { ...a.months, [tgtMonth]: a.months[srcMonth] || 0 },
          })),
        }));
      }
      colCopyDragRef.current = null;
      colCopyTargetRef.current = null;
      setColCopyState(null);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mouseup', onUp);
  }

  function onColHeaderEnter(monthIdx) {
    if (!colCopyDragRef.current) return;
    colCopyTargetRef.current = monthIdx;
    setColCopyState(prev => prev ? { ...prev, targetIdx: monthIdx } : null);
  }

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const [offerSnap, userSnap, deptSnap] = await Promise.all([
      getDoc(doc(db, 'offers', offerId)),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]);
    if (offerSnap.exists()) {
      const data = { id: offerSnap.id, ...offerSnap.data() };
      setOffer(data);
      const loadedBudgets = data.budgets || [];
      setBudgets(loadedBudgets);
      setActiveBudgetId(loadedBudgets[0]?.id || null);
    }
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, [offerId]);

  useEffect(() => { load(); }, [load]);

  // ── Budget helpers ─────────────────────────────────────────────────────────
  const activeBudget = budgets.find(b => b.id === activeBudgetId) || null;

  function updateActiveBudget(updater) {
    setBudgets(prev => prev.map(b => b.id === activeBudgetId ? { ...b, ...updater(b) } : b));
  }

  function handleAddBudget() {
    const newB = {
      id: uid(),
      name: `Budget ${budgets.length + 1}`,
      startMonth: offer?.startDate ? offer.startDate.slice(0, 7) : '',
      endMonth: offer?.endDate ? offer.endDate.slice(0, 7) : '',
      resources: [],
      activities: [],
      type: 'offert',
    };
    setBudgets(prev => [...prev, newB]);
    setActiveBudgetId(newB.id);
  }

  async function handleSave() {
    setSaving(true);
    const budgetsToSave = budgets.map(b =>
      b.id === activeBudgetId ? { ...b, actColW: actColW ?? null, resColW } : b
    );
    await updateDoc(doc(db, 'offers', offerId), { budgets: budgetsToSave, updatedAt: new Date() });
    setBudgets(budgetsToSave);
    setSaving(false);
  }

  // ── Resource handlers ──────────────────────────────────────────────────────
  function handleAddPerson(userId) {
    if (!userId) return;
    const user = users.find(u => u.id === userId);
    updateActiveBudget(b => ({
      resources: [...b.resources, {
        id: uid(),
        userId,
        name: user?.displayName || user?.name || user?.email || '',
        shortName: user?.shortName || '',
        hourlyRate: '',
      }],
    }));
    setNewResourceUserId('');
  }

  function handleAddRole() {
    const name = newRoleName.trim() || 'Roll';
    updateActiveBudget(b => ({
      resources: [...b.resources, { id: uid(), name, shortName: '', hourlyRate: '', isRole: true }],
    }));
    setNewRoleName('');
  }

  function handleResourceChange(id, field, value) {
    updateActiveBudget(b => ({ resources: b.resources.map(r => r.id === id ? { ...r, [field]: value } : r) }));
  }

  function handleDeleteResource(id) {
    updateActiveBudget(b => ({
      resources: b.resources.filter(r => r.id !== id),
      activities: b.activities.filter(a => a.resourceId !== id),
    }));
  }

  // ── Activity handlers ──────────────────────────────────────────────────────
  function handleAddActivity() {
    const months = budgetMonths;
    const mo = {};
    months.forEach(m => { mo[m] = 0; });
    updateActiveBudget(b => ({ activities: [...b.activities, { id: uid(), name: '', resourceId: '', months: mo }] }));
  }

  function handleActivityChange(id, field, value) {
    updateActiveBudget(b => ({ activities: b.activities.map(a => a.id === id ? { ...a, [field]: value } : a) }));
  }

  function handleActivityMonthChange(id, month, value) {
    updateActiveBudget(b => ({
      activities: b.activities.map(a =>
        a.id === id ? { ...a, months: { ...a.months, [month]: parseFloat(value) || 0 } } : a
      ),
    }));
  }

  function handleDeleteActivity(id) {
    updateActiveBudget(b => ({ activities: b.activities.filter(a => a.id !== id) }));
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const budgetResources = activeBudget?.resources || [];
  const budgetActivities = activeBudget?.activities || [];
  const budgetMonths = generateMonths(activeBudget?.startMonth || '', activeBudget?.endMonth || '');

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!offer) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">Offerten hittades inte.</Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={() => navigate(`/offers/${offerId}`)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">{offer.title || offer.name || '(Namnlös offert)'}</Typography>
            <Typography variant="body2" color="text.secondary">Offertbudget</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            {saving ? 'Sparar…' : 'Spara budget'}
          </Button>
        </Box>

        {/* Budget version tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {budgets.map(b => (
            <Chip
              key={b.id}
              label={b.name}
              onClick={() => setActiveBudgetId(b.id)}
              variant={b.id === activeBudgetId ? 'filled' : 'outlined'}
              sx={{
                bgcolor: b.id === activeBudgetId ? '#1a1a1a' : undefined,
                color: b.id === activeBudgetId ? 'white' : undefined,
                cursor: 'pointer',
              }}
            />
          ))}
          <Chip
            label="+ Ny budget"
            variant="outlined"
            onClick={handleAddBudget}
            sx={{ cursor: 'pointer', borderStyle: 'dashed' }}
          />
          {budgets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Inga budgetar ännu. Skapa en med knappen ovan.
            </Typography>
          )}
        </Box>

        {activeBudget && (
          <>
            {/* Budget meta */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                label="Budgetnamn"
                size="small"
                value={activeBudget.name || ''}
                onChange={e => updateActiveBudget(() => ({ name: e.target.value }))}
                sx={{ width: 200 }}
              />
              <TextField
                label="Startmånad"
                size="small"
                placeholder="ÅÅÅÅ-MM"
                value={activeBudget.startMonth || ''}
                onChange={e => updateActiveBudget(() => ({ startMonth: e.target.value }))}
                sx={{ width: 140 }}
              />
              <TextField
                label="Slutmånad"
                size="small"
                placeholder="ÅÅÅÅ-MM"
                value={activeBudget.endMonth || ''}
                onChange={e => updateActiveBudget(() => ({ endMonth: e.target.value }))}
                sx={{ width: 140 }}
              />
              {(offer.startDate || offer.endDate) && (
                <Button size="small" variant="outlined" sx={{ height: 40 }}
                  onClick={() => updateActiveBudget(() => ({
                    startMonth: offer.startDate ? offer.startDate.slice(0, 7) : activeBudget.startMonth,
                    endMonth: offer.endDate ? offer.endDate.slice(0, 7) : activeBudget.endMonth,
                  }))}>
                  Hämta från offert
                </Button>
              )}
            </Paper>

            {/* Resurser & Roller */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Resurser & Roller</Typography>
                  <Chip label="Person = konkret medarbetare" size="small"
                    icon={<PersonIcon sx={{ fontSize: 14 }} />}
                    sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontSize: '0.7rem', height: 22 }} />
                  <Chip label="Roll = fritext (offert)" size="small"
                    icon={<WorkIcon sx={{ fontSize: 14 }} />}
                    sx={{ bgcolor: '#F3E5F5', color: '#6A1B9A', fontSize: '0.7rem', height: 22 }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ToggleButtonGroup size="small" exclusive value={addMode} onChange={(_, v) => v && setAddMode(v)}
                    sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1.5, fontSize: '0.75rem' } }}>
                    <ToggleButton value="person">
                      <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} /> Person
                    </ToggleButton>
                    <ToggleButton value="role">
                      <WorkIcon sx={{ fontSize: 14, mr: 0.5 }} /> Roll
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {addMode === 'person' ? (
                    <>
                      <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={newResourceUserId}
                          onChange={e => setNewResourceUserId(e.target.value)}
                          displayEmpty
                          renderValue={val => {
                            if (!val) return <span style={{ color: '#aaa' }}>Välj medarbetare…</span>;
                            const u = users.find(u => u.id === val);
                            return u?.displayName || u?.name || u?.email || val;
                          }}>
                          <MenuItem value=""><em>– Välj medarbetare –</em></MenuItem>
                          {departments.map(dept => {
                            const deptUsers = users.filter(u => u.departmentId === dept.id
                              && !budgetResources.some(r => r.userId === u.id));
                            if (!deptUsers.length) return null;
                            return [
                              <ListSubheader key={`hdr-${dept.id}`} sx={{ fontSize: '0.7rem', lineHeight: '28px', bgcolor: '#fafafa' }}>
                                {dept.offerCode ? `${dept.offerCode} – ${dept.name}` : dept.name}
                              </ListSubheader>,
                              ...deptUsers.map(u => (
                                <MenuItem key={u.id} value={u.id} sx={{ pl: 3 }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                      {u.displayName || u.name || u.email}
                                      {u.shortName && <span style={{ color: '#999', fontSize: '0.7rem', marginLeft: 6 }}>{u.shortName}</span>}
                                    </Typography>
                                    {u.title && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{u.title}</Typography>}
                                  </Box>
                                </MenuItem>
                              )),
                            ];
                          })}
                        </Select>
                      </FormControl>
                      <Button size="small" startIcon={<AddIcon />}
                        onClick={() => handleAddPerson(newResourceUserId)}
                        disabled={!newResourceUserId}
                        sx={{ bgcolor: '#1565C0', color: 'white', '&:hover': { bgcolor: '#0D47A1' }, whiteSpace: 'nowrap' }}>
                        Lägg till person
                      </Button>
                    </>
                  ) : (
                    <>
                      <TextField
                        size="small"
                        placeholder="Rollnamn, t.ex. Projektledare…"
                        value={newRoleName}
                        onChange={e => setNewRoleName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddRole()}
                        sx={{ width: 220 }}
                      />
                      <Button size="small" startIcon={<AddIcon />}
                        onClick={handleAddRole}
                        sx={{ bgcolor: '#6A1B9A', color: 'white', '&:hover': { bgcolor: '#4A148C' }, whiteSpace: 'nowrap' }}>
                        Lägg till roll
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f5f5f5' } }}>
                    <TableCell>Person / Roll</TableCell>
                    <TableCell>Timpris (kr/tim)</TableCell>
                    <TableCell align="right">Totalt timmar</TableCell>
                    <TableCell align="right">Totalt värde</TableCell>
                    <TableCell width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetResources.map(res => {
                    const isRole = res.isRole || !res.userId;
                    const totalHrs = budgetActivities
                      .filter(a => a.resourceId === res.id)
                      .reduce((s, a) => s + budgetMonths.reduce((ms, m) => ms + (parseFloat(a.months?.[m]) || 0), 0), 0);
                    const totalVal = totalHrs * (parseFloat(res.hourlyRate) || 0);
                    return (
                      <TableRow key={res.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={isRole ? 'Roll' : 'Person'}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem',
                                bgcolor: isRole ? '#F3E5F5' : '#E3F2FD',
                                color: isRole ? '#6A1B9A' : '#1565C0',
                              }}
                            />
                            {isRole ? (
                              <TextField
                                variant="standard" size="small"
                                value={res.name}
                                onChange={e => handleResourceChange(res.id, 'name', e.target.value)}
                                placeholder="Rollnamn"
                                sx={{ width: 180 }}
                              />
                            ) : (
                              <Box>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                  {res.name || <span style={{ color: '#aaa' }}>(namnlös)</span>}
                                </Typography>
                                {res.shortName && (
                                  <Typography sx={{ fontSize: '0.68rem', color: '#888', letterSpacing: 1 }}>
                                    {res.shortName}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" size="small" type="number"
                            value={res.hourlyRate}
                            onChange={e => handleResourceChange(res.id, 'hourlyRate', e.target.value)}
                            inputProps={{ style: { textAlign: 'right' } }}
                            placeholder="0" sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{totalHrs.toFixed(1)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#1B5E20' }}>
                            {fmtSEK(totalVal)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleDeleteResource(res.id)}
                            sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {budgetResources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          Inga resurser. Lägg till en person eller roll ovan.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {budgetResources.length > 0 && (() => {
                    const grandHrs = budgetResources.reduce((s, res) =>
                      s + budgetActivities.filter(a => a.resourceId === res.id)
                        .reduce((as, a) => as + budgetMonths.reduce((ms, m) => ms + (parseFloat(a.months?.[m]) || 0), 0), 0), 0);
                    const grandVal = budgetResources.reduce((s, res) => {
                      const hrs = budgetActivities.filter(a => a.resourceId === res.id)
                        .reduce((as, a) => as + budgetMonths.reduce((ms, m) => ms + (parseFloat(a.months?.[m]) || 0), 0), 0);
                      return s + hrs * (parseFloat(res.hourlyRate) || 0);
                    }, 0);
                    return (
                      <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: '#f9f9f9' } }}>
                        <TableCell colSpan={2}>Totalt</TableCell>
                        <TableCell align="right">{grandHrs.toFixed(1)} tim</TableCell>
                        <TableCell align="right" sx={{ color: '#FF6B00' }}>{fmtSEK(grandVal)}</TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </Paper>

            {/* Aktiviteter per månad */}
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight="bold">Aktiviteter per månad</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ToggleButtonGroup size="small" exclusive
                    value={resNameMode} onChange={(_, v) => v && setResNameMode(v)}
                    sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.7rem', lineHeight: 1.4 } }}>
                    <ToggleButton value="first">Förnamn</ToggleButton>
                    <ToggleButton value="full">Hela</ToggleButton>
                    <ToggleButton value="abbr" title="Förkortning">Förk.</ToggleButton>
                  </ToggleButtonGroup>
                  <Button size="small" startIcon={<AddIcon />}
                    onClick={handleAddActivity}
                    disabled={budgetMonths.length === 0}
                    sx={{ bgcolor: '#1a1a1a', color: 'white', '&:hover': { bgcolor: '#333' } }}>
                    Lägg till aktivitet
                  </Button>
                </Box>
              </Box>

              {budgetMonths.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  Ange period ovan för att börja lägga till aktiviteter.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f5f5f5' } }}>
                        <TableCell ref={actColRef} sx={{
                          position: 'sticky', left: 0, zIndex: 2, bgcolor: '#f5f5f5', p: 0,
                          ...(actColW !== null ? { width: actColW, minWidth: actColW, maxWidth: actColW } : { minWidth: 100 }),
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
                            <span style={{ flex: 1 }}>Aktivitet</span>
                            <Box onMouseDown={e => startColResize('act', e)}
                              onDoubleClick={() => setActColW(null)}
                              title="Dra för att ändra bredd"
                              sx={{ width: 5, cursor: 'col-resize', alignSelf: 'stretch', flexShrink: 0,
                                borderRight: '2px solid #ddd', '&:hover': { borderRight: '2px solid #1a1a1a' } }} />
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                          position: 'sticky', left: actColW !== null ? actColW : actColRenderedW, zIndex: 2,
                          bgcolor: '#f5f5f5', width: resColW, minWidth: resColW, maxWidth: resColW, p: 0,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
                            <span style={{ flex: 1 }}>Resurs</span>
                            <Box onMouseDown={e => startColResize('res', e)}
                              sx={{ width: 5, cursor: 'col-resize', alignSelf: 'stretch', flexShrink: 0,
                                borderRight: '2px solid #ddd', '&:hover': { borderRight: '2px solid #1a1a1a' } }} />
                          </Box>
                        </TableCell>
                        {budgetMonths.map((m, mi) => {
                          const isSrc = colCopyState?.sourceIdx === mi;
                          const isTgt = colCopyState && colCopyState.targetIdx === mi && colCopyState.targetIdx !== colCopyState.sourceIdx;
                          return (
                            <TableCell key={m} align="center"
                              onMouseEnter={() => onColHeaderEnter(mi)}
                              sx={{ width: 44, minWidth: 44, whiteSpace: 'nowrap', fontSize: '0.7rem', p: '4px 2px',
                                bgcolor: isSrc ? '#FFF3E0' : isTgt ? '#E3F2FD' : '#f5f5f5' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                {fmtMonth(m)}
                                <Box onMouseDown={e => startColCopyDrag(mi, budgetMonths, e)}
                                  title="Dra för att kopiera kolumn"
                                  sx={{ width: 20, height: 4, borderRadius: 1,
                                    bgcolor: isSrc ? '#FF6B00' : '#ccc',
                                    cursor: 'ew-resize', '&:hover': { bgcolor: '#1565C0' } }} />
                              </Box>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Totalt tim</TableCell>
                        <TableCell align="right">Värde</TableCell>
                        <TableCell width={40} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {budgetActivities.map(act => {
                        const actHrs = budgetMonths.reduce((s, m) => s + (parseFloat(act.months[m]) || 0), 0);
                        const res = budgetResources.find(r => r.id === act.resourceId);
                        const actVal = actHrs * (parseFloat(res?.hourlyRate) || 0);
                        return (
                          <TableRow key={act.id}>
                            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', overflow: 'hidden',
                              ...(actColW !== null ? { width: actColW, minWidth: actColW, maxWidth: actColW } : { minWidth: 100 }) }}>
                              <TextField variant="standard" size="small" value={act.name}
                                onChange={e => handleActivityChange(act.id, 'name', e.target.value)}
                                placeholder="Aktivitet" sx={{ width: '100%' }} />
                            </TableCell>
                            <TableCell sx={{ position: 'sticky', left: actColW !== null ? actColW : actColRenderedW,
                              zIndex: 1, bgcolor: 'background.paper', width: resColW, minWidth: resColW, maxWidth: resColW, overflow: 'hidden' }}>
                              <FormControl variant="standard" size="small" sx={{ width: '100%' }}>
                                <Select
                                  value={act.resourceId}
                                  onChange={e => handleActivityChange(act.id, 'resourceId', e.target.value)}
                                  displayEmpty
                                  renderValue={val => {
                                    if (!val) return <em style={{ color: '#aaa' }}>– Välj –</em>;
                                    const r = budgetResources.find(r => r.id === val);
                                    return fmtResName(r, resNameMode);
                                  }}>
                                  <MenuItem value=""><em>– Välj –</em></MenuItem>
                                  {budgetResources.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {(r.isRole || !r.userId) && (
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9C27B0', flexShrink: 0 }} />
                                        )}
                                        {r.name || '(namnlös)'}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            {budgetMonths.map((m, mi) => {
                              const inFill = fillHighlight?.actId === act.id
                                && mi >= fillHighlight.fromIdx && mi <= fillHighlight.toIdx;
                              const inColSrc = colCopyState?.sourceIdx === mi;
                              const inColTgt = colCopyState && colCopyState.targetIdx === mi && colCopyState.targetIdx !== colCopyState.sourceIdx;
                              return (
                                <TableCell key={m} align="center"
                                  onMouseEnter={() => { onFillCellEnter(act.id, mi); onColHeaderEnter(mi); }}
                                  sx={{ p: '4px 2px', position: 'relative',
                                    bgcolor: inFill ? 'rgba(21,101,192,0.10)' : inColSrc ? 'rgba(255,107,0,0.07)' : inColTgt ? 'rgba(21,101,192,0.10)' : undefined,
                                    outline: inFill ? '1px dashed #1565C0' : undefined,
                                    '&:hover .cell-fill-handle': { opacity: 1 } }}>
                                  <TextField variant="standard" size="small"
                                    value={act.months[m] || ''}
                                    onChange={e => {
                                      const v = e.target.value.replace(/[^0-9.]/g, '');
                                      handleActivityMonthChange(act.id, m, v);
                                    }}
                                    inputProps={{ style: { textAlign: 'center', fontSize: '0.75rem', padding: '2px 0' } }}
                                    sx={{ width: 40, '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                                  />
                                  {/* Fill handle */}
                                  <Box className="cell-fill-handle"
                                    onMouseDown={e => startFillDrag(act.id, mi, act.months[m] || 0, budgetMonths, e)}
                                    sx={{ position: 'absolute', bottom: 1, right: 1, width: 7, height: 7,
                                      bgcolor: '#1565C0', borderRadius: '1px', opacity: 0, cursor: 'crosshair' }} />
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{actHrs.toFixed(1)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" sx={{ color: '#1B5E20', whiteSpace: 'nowrap' }}>
                                {fmtSEK(actVal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleDeleteActivity(act.id)}
                                sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {budgetActivities.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={budgetMonths.length + 5} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              Inga aktiviteter. Lägg till en ovan.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {budgetActivities.length > 0 && (() => {
                        const grandHrs = budgetMonths.reduce((s, m) =>
                          s + budgetActivities.reduce((as, a) => as + (parseFloat(a.months[m]) || 0), 0), 0);
                        const grandVal = budgetResources.reduce((s, res) => {
                          const hrs = budgetActivities.filter(a => a.resourceId === res.id)
                            .reduce((as, a) => as + budgetMonths.reduce((ms, m) => ms + (parseFloat(a.months?.[m]) || 0), 0), 0);
                          return s + hrs * (parseFloat(res.hourlyRate) || 0);
                        }, 0);
                        return (
                          <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: '#f9f9f9' } }}>
                            <TableCell colSpan={2}>Totalt</TableCell>
                            {budgetMonths.map(m => {
                              const colHrs = budgetActivities.reduce((s, a) => s + (parseFloat(a.months[m]) || 0), 0);
                              return (
                                <TableCell key={m} align="center" sx={{ fontSize: '0.75rem' }}>
                                  {colHrs > 0 ? colHrs.toFixed(0) : ''}
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">{grandHrs.toFixed(1)} tim</TableCell>
                            <TableCell align="right" sx={{ color: '#FF6B00' }}>{fmtSEK(grandVal)}</TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
}
